import { useState } from 'react';
import { useT, useUiStore } from '@/store';
import { formatOrderDateTime } from '@/lib/format';
import { Modal } from '@/components/ui/Overlay';
import { ErrorState } from '@/components/ui/Panel';
import { ConfirmButton } from '@/components/ui/ConfirmButton';

/**
 * Issues a client's portal sign-in link and puts it on screen to be copied.
 *
 * This is the missing half of a feature that was otherwise finished. The
 * route (POST /api/clients/<id>/magic-link), the session model
 * (core/client_auth), the portal bundle, the approvals queue it feeds and
 * even the frontend hook — useIssueMagicLink() in hooks/useClientRequests —
 * were all written and tested. No component imported that hook, so there was
 * no way for a dispatcher to let a client in, and roughly 1,200 lines of
 * working B2B portal sat unreachable.
 *
 * Same shape as DriverKeyModal for the same reason: this is a one-time
 * credential the server returns once and never again, so it gets a modal the
 * admin dismisses deliberately with an explicit copy button, not a toast
 * that can expire while they are looking at their phone.
 *
 * The link is made absolute here. The backend builds it from
 * ARAGELA_CLIENT_PORTAL_URL and falls back to the relative "/client.html",
 * and its own docstring notes that a relative URL is "useless the moment it
 * is pasted into a text message, which is exactly how these links travel" —
 * so the console, which is the thing that knows its own origin, supplies it.
 *
 * V-71 added the other half: taking access away. useRevokeClientSessions()
 * had been written and tested and no component imported it, so a lost phone
 * or a site manager who left the company had no answer in the console at
 * all. It belongs here rather than on the row because it is the same
 * subject as the button beside it — who can sign in — and because it must
 * be read against the archive action, not confused with it: revoking signs
 * the client out everywhere and leaves them ACTIVE, so the next link lets
 * them straight back in. Ending the relationship is the archive button in
 * the list.
 */
export function ClientAccessModal({ client, issue, revoke, onClose }) {
  const t = useT();
  const lang = useUiStore((s) => s.lang);
  const [copied, setCopied] = useState(false);

  const issued = issue.data;
  const absoluteUrl =
    issued?.url && issued.url.startsWith('/') ? `${window.location.origin}${issued.url}` : issued?.url;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(absoluteUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      // Clipboard needs a secure context; the link is on screen either way.
    }
  };

  const close = () => {
    setCopied(false);
    issue.reset?.();
    revoke?.reset?.();
    onClose();
  };

  // The route answers {sessions_revoked, pending_links_invalidated}, and
  // both numbers are the answer rather than decoration. Zero sessions is a
  // real and common result — the client simply was not signed in anywhere —
  // and reporting it as "done" would leave a dispatcher believing they had
  // just closed a door that was never open. Unused links dying matters for
  // the opposite reason: a link sent this morning and not yet tapped stops
  // working here, and without a word about it the client's failed tap looks
  // like a broken product.
  const revoked = revoke?.data;

  return (
    <Modal
      open={Boolean(client)}
      onClose={close}
      title={t('clients.accessTitle')}
      footer={
        <button type="button" className="btn btn-primary" onClick={close}>
          {t('common.closeBtn')}
        </button>
      }
    >
      {client ? (
        <div className="space-y-3">
          <p className="text-[12.5px]">{client.client_name}</p>

          {issue.isError ? <ErrorState error={issue.error} /> : null}

          {issued ? (
            <>
              <div className="flex gap-2">
                <code className="flex-1 break-all rounded-[9px] border border-line bg-white/4 px-3 py-2 text-[12px]">
                  {absoluteUrl}
                </code>
                <button type="button" className="btn" onClick={copy}>
                  {copied ? t('common.addedCheck') : t('drivers.copyKey')}
                </button>
              </div>

              {/* Both facts matter to whoever is about to paste this into a
                  message: it stops working once used, and it expires. A
                  dispatcher who does not know the first will wonder why the
                  client's second tap fails. */}
              <p className="rounded-[10px] border border-amber/25 bg-amber/8 px-3 py-2 text-[11.5px] leading-snug text-amber">
                {t('clients.accessWarning')}
              </p>
              {/* V-363. This was `{issued.expires_at}` — the raw column, which
                  core/client_auth.py's _iso() documents as fixed-width UTC
                  ("YYYY-MM-DDTHH:MM:SS+00:00"). lib/format.js already names
                  this exact failure on formatDocumentTime(): "Never a slice
                  of the ISO string: iso.slice(11, 16) is UTC and reads three
                  hours early in summer." Printing the whole string is the
                  same error one step louder.

                  It matters here more than almost anywhere else in the
                  product because MAGIC_LINK_TTL_MINUTES is 30. A dispatcher
                  who issues a link at 19:45 local read "17:15" off this line
                  and had to conclude it died two and a half hours ago, while
                  it had half an hour left — and the whole point of the line
                  is to tell them what to say to the client.

                  formatOrderDateTime() rather than the formatDocument*
                  pair: this is an operational time a dispatcher reads on
                  their own clock, not a figure frozen onto a document, and
                  it is what the other twenty files that show a timestamp on
                  screen use. */}
              <p className="text-[11px] text-dim">
                {t('clients.accessExpires')}: {formatOrderDateTime(issued.expires_at, lang)}
              </p>
            </>
          ) : (
            <>
              <p className="text-[11.5px] leading-snug text-dim">{t('clients.accessHint')}</p>
              <button
                type="button"
                className="btn btn-primary"
                disabled={issue.isPending}
                onClick={() => issue.mutate({ clientId: client.client_id })}
              >
                {t(issue.isPending ? 'common.loading' : 'clients.accessIssueBtn')}
              </button>
            </>
          )}

          {revoke ? (
            <div className="border-t border-line pt-3">
              <p className="mb-2 text-[11.5px] leading-snug text-dim">{t('clients.revokeHint')}</p>
              {revoke.isError ? <ErrorState error={revoke.error} /> : null}
              {revoked ? (
                <p
                  role="status"
                  className={`mb-2 text-[11.5px] font-semibold leading-snug ${
                    revoked.sessions_revoked > 0 ? 'text-green' : 'text-amber'
                  }`}
                >
                  {revoked.sessions_revoked > 0
                    ? t('clients.revokedN', { n: revoked.sessions_revoked })
                    : t('clients.revokedNone')}
                  {revoked.pending_links_invalidated > 0
                    ? ` ${t('clients.revokedLinksN', { n: revoked.pending_links_invalidated })}`
                    : ''}
                </p>
              ) : null}
              <ConfirmButton
                label={t('clients.revokeBtn')}
                confirmLabel={t('clients.revokeConfirm')}
                disabled={revoke.isPending}
                onConfirm={() => revoke.mutate({ clientId: client.client_id })}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}
